import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "react-oauth2-code-pkce";
import AIIconFullCBlack from "../../assets/AI_Icon_fullC_black.svg";
import AiChartPlaceholder from "../../assets/ai-chart-placeholder.svg";
import { getAppEnv } from "../../lib/getEnvVariables";
import useGlobalStore from "../../store/useGlobalStore";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import AiBarChart from "./AiBarChart";
import AiLineChart from "./AiLineChart";
import AiPieChart from "./AiPieChart";
import { parseChartPayload } from "./aiChartUtils";
/**
 * Origins that the cat-ai-assistant iframe is allowed to post messages from.
 * Mirrors the env→URL mapping used by the web component.
 */
const TRUSTED_AI_ASSISTANT_ORIGINS = new Set([
    "https://dev-ai-assistant.cat.com",
    "https://int-ai-assistant.cat.com",
    "https://ai-assistant.cat.com"
]);
/** Safety cap: discard a streaming buffer that grows beyond this size (bytes). */
const CHART_BUFFER_MAX_BYTES = 512 * 1024; // 512 KB
const AiAssistantDispatchEvent = {
    INVALID_CONFIGURATION: "ai-assistant-invalid-configuration",
    LOADED: "ai-assistant-loaded",
    NOTIFICATION_EVENT: "ai-assistant-notification-event",
    REDIRECTION_LOGIN: "ai-assistant-redirection-login",
    TOKEN_EXPIRED: "ai-assistant-token-expired",
    TOKEN_INVALID: "ai-assistant-token-invalid",
    TOKEN_PENDING_EXPIRATION: "ai-assistant-token-pending-expiration"
};
const AiAssistant = () => {
    const { logIn, token } = useContext(AuthContext);
    const [chartPayload, setChartPayload] = useState(null);
    const chartBufferRef = useRef("");
    const { primaryPartyType, userPartyNumber } = useGlobalStore((state) => ({
        primaryPartyType: state.primaryPartyType,
        userPartyNumber: state.userPartyNumber
    }));
    const { projectDetails } = useProjectDetailsStore((state) => ({
        projectDetails: state.projectDetails
    }));
    const resolvedAccessToken = useMemo(() => {
        if (token) {
            return token;
        }
        try {
            const stored = localStorage.getItem("access_token");
            if (!stored)
                return "";
            const parsed = JSON.parse(stored);
            return typeof parsed === "string" && parsed.length > 0 ? parsed : "";
        }
        catch {
            return "";
        }
    }, [token]);
    /**
     * Constructs user preferences object for AI Assistant
     * Includes language and other user/default preferences in base64 encoded JSON format
     */
    const aiAssistantUserPreferences = useMemo(() => {
        const userProvided = {
            language: "en-US"
        };
        const defaults = {
            assetLabel: "name",
            assetLocationLabel: "address",
            currency: "USD",
            dateFormat: "ddmmyyyy",
            language: "en-US",
            numberFormat: "radixComa",
            pressureUnit: "kpa",
            systemOfMeasurement: "metric",
            temperatureUnit: "C",
            timeFormat: "hours12",
            timeZone: "UTC",
            windowsTimeZone: "GMT Standard Time"
        };
        return btoa(JSON.stringify({ defaults, userProvided }));
    }, []);
    /**
     * Constructs application context for AI Assistant
     * Currently includes the application name identifier
     */
    const aiAssistantAppContext = useMemo(() => {
        const isCatUser = primaryPartyType?.toUpperCase() === "CAT";
        const location = projectDetails?.streetAddress || "";
        const projectName = projectDetails?.projectName || "";
        return JSON.stringify({
            appName: "eflow.cat.com",
            conditions: {
                filters: {
                    downSelectPartyNumber: isCatUser ? userPartyNumber || "" : "",
                    location,
                    partyNumber: !isCatUser ? userPartyNumber || "" : "",
                    site_name: projectName,
                    subscribed: true
                }
            }
        });
    }, [primaryPartyType, projectDetails?.projectName, projectDetails?.streetAddress, userPartyNumber]);
    /**
     * Maps the app environment to the corresponding AI Assistant environment string.
     * The web component uses these values to construct the iframe URL:
     *   local → dev  (AI assistant not running locally; use dev instance)
     *   dev   → https://dev-ai-assistant.cat.com
     *   int   → https://int-ai-assistant.cat.com
     *   prod  → https://ai-assistant.cat.com
     */
    const aiAssistantEnv = useMemo(() => {
        const envMap = {
            dev: "dev",
            int: "int",
            local: "dev", // AI assistant not running locally; use dev instance
            prod: "prod"
        };
        return envMap[getAppEnv()] ?? "dev";
    }, []);
    /**
     * Auth event listeners — re-registered whenever logIn changes so the latest
     * reference is used when redirecting to the OAuth login page.
     */
    useEffect(() => {
        const handleTokenExpired = () => {
            if (logIn)
                logIn();
        };
        const handleTokenInvalid = () => {
            if (logIn)
                logIn();
        };
        const handleRedirectionLogin = () => {
            if (logIn)
                logIn();
        };
        const handleTokenPendingExpiration = () => {
            // react-oauth2-code-pkce handles silent token refresh automatically
        };
        const handleInvalidConfiguration = () => { };
        const handleLoaded = () => { };
        window.addEventListener(AiAssistantDispatchEvent.TOKEN_EXPIRED, handleTokenExpired);
        window.addEventListener(AiAssistantDispatchEvent.TOKEN_PENDING_EXPIRATION, handleTokenPendingExpiration);
        window.addEventListener(AiAssistantDispatchEvent.TOKEN_INVALID, handleTokenInvalid);
        window.addEventListener(AiAssistantDispatchEvent.REDIRECTION_LOGIN, handleRedirectionLogin);
        window.addEventListener(AiAssistantDispatchEvent.INVALID_CONFIGURATION, handleInvalidConfiguration);
        window.addEventListener(AiAssistantDispatchEvent.LOADED, handleLoaded);
        return () => {
            window.removeEventListener(AiAssistantDispatchEvent.TOKEN_EXPIRED, handleTokenExpired);
            window.removeEventListener(AiAssistantDispatchEvent.TOKEN_PENDING_EXPIRATION, handleTokenPendingExpiration);
            window.removeEventListener(AiAssistantDispatchEvent.TOKEN_INVALID, handleTokenInvalid);
            window.removeEventListener(AiAssistantDispatchEvent.REDIRECTION_LOGIN, handleRedirectionLogin);
            window.removeEventListener(AiAssistantDispatchEvent.INVALID_CONFIGURATION, handleInvalidConfiguration);
            window.removeEventListener(AiAssistantDispatchEvent.LOADED, handleLoaded);
        };
    }, [logIn]);
    const dismissChart = useCallback(() => {
        setChartPayload(null);
    }, []);
    /**
     * Chart data listener — registered once on mount, never torn down during auth refreshes.
     * Accumulates streaming datapassthrough_delta chunks from ai-assistant-notification-event
     * and parses them into a chart payload once the JSON is complete.
     */
    useEffect(() => {
        // Raw postMessage channel — bypasses the web component's 100ms dedup so every
        // streaming chunk arrives. Path confirmed: event.data.data.details.data
        let pmBuffer = "";
        const handleMessage = (event) => {
            if (!TRUSTED_AI_ASSISTANT_ORIGINS.has(event.origin))
                return;
            if (!event.data || typeof event.data !== "object")
                return;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (event.data?.source !== "mfe-application")
                return;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const delta = event.data?.data?.details?.data;
            if (delta?.type === "datapassthrough_delta" &&
                delta?.topic === "chart" &&
                typeof delta?.payload === "string") {
                pmBuffer += delta.payload;
                if (pmBuffer.length > CHART_BUFFER_MAX_BYTES) {
                    pmBuffer = "";
                    return;
                }
                const chart = parseChartPayload(pmBuffer);
                if (chart) {
                    setChartPayload(chart);
                    pmBuffer = "";
                    chartBufferRef.current = "";
                }
            }
        };
        // Notification event channel — deduplicated to ~1 per 100ms by the web component,
        // so only used as a fallback if postMessage does not carry chart data.
        const handleNotification = (event) => {
            const detail = event.detail;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const delta = detail?.data?.details?.data;
            if (delta?.type === "datapassthrough_delta" &&
                delta?.topic === "chart" &&
                typeof delta?.payload === "string") {
                chartBufferRef.current += delta.payload;
                if (chartBufferRef.current.length > CHART_BUFFER_MAX_BYTES) {
                    chartBufferRef.current = "";
                    return;
                }
                const chart = parseChartPayload(chartBufferRef.current);
                if (chart) {
                    setChartPayload(chart);
                    chartBufferRef.current = "";
                    pmBuffer = "";
                }
            }
        };
        window.addEventListener("message", handleMessage);
        window.addEventListener(AiAssistantDispatchEvent.NOTIFICATION_EVENT, handleNotification);
        return () => {
            window.removeEventListener("message", handleMessage);
            window.removeEventListener(AiAssistantDispatchEvent.NOTIFICATION_EVENT, handleNotification);
            pmBuffer = "";
            chartBufferRef.current = "";
        };
    }, []);
    const renderedChart = useMemo(() => {
        if (!chartPayload)
            return null;
        if (chartPayload.plot_type === "line" || chartPayload.plot_type === "bar") {
            if (!Array.isArray(chartPayload.series) || chartPayload.series.length === 0)
                return null;
            if (chartPayload.plot_type === "bar") {
                return (_jsx(AiBarChart, { series: chartPayload.series, title: chartPayload.title, x_label: chartPayload.x_label, y_label: chartPayload.y_label }));
            }
            return (_jsx(AiLineChart, { series: chartPayload.series, title: chartPayload.title, x_label: chartPayload.x_label, y_label: chartPayload.y_label }));
        }
        const { colors, labels, title, values } = chartPayload;
        if (!Array.isArray(labels) || !Array.isArray(values) || labels.length === 0 || values.length === 0) {
            return null;
        }
        return _jsx(AiPieChart, { colors: colors, labels: labels, title: title, values: values });
    }, [chartPayload]);
    return (_jsxs("div", { "data-test": "ai-insights-content", children: [_jsxs("div", { className: "flex items-center gap-2 mb-8", "data-test": "cat-ai-assistant-header", children: [_jsx("img", { alt: "AI Insights icon", className: "w-10 h-10", src: AIIconFullCBlack }), _jsx("h1", { className: "text-2xl font-bold tracking-tight", children: "AI Insights" }), renderedChart && (_jsx("button", { "aria-label": "Dismiss chart", className: "ml-4 text-xs text-gray-400 hover:text-gray-600", "data-test": "ai-chart-dismiss-btn", onClick: dismissChart, type: "button", children: "\u2715 Dismiss chart" }))] }), _jsx("div", { "data-test": "ai-insights-content-container", children: renderedChart ? (
                // lg:pr-[380px] reserves space so Plotly sizes within the visible area (cat-ai-assistant is a fixed-right sidebar ~380px wide)
                _jsx("div", { className: "w-full lg:pr-[380px]", "data-test": "ai-chart-container", children: renderedChart })) : (_jsxs("div", { className: "flex flex-col items-center justify-center py-16 text-gray-400 w-full", "data-test": "ai-chart-empty-state", children: [_jsx("img", { alt: "", "aria-hidden": "true", src: AiChartPlaceholder }), _jsx("p", { className: "mt-4 text-sm font-medium text-gray-500", children: "Use the AI assistant to ask questions about your project" })] })) }), _jsx("div", { "data-test": "ai-insights-panel", children: resolvedAccessToken &&
                    projectDetails?.projectId &&
                    projectDetails?.projectName &&
                    primaryPartyType && (_jsx(Fragment, { children: _jsx("cat-ai-assistant", { "access-token": resolvedAccessToken, "app-context": aiAssistantAppContext, "data-test": "cat-ai-assistant", environment: aiAssistantEnv, "x-cat-user-preferences": aiAssistantUserPreferences }) }, projectDetails.projectId)) })] }));
};
export default AiAssistant;
